import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../api';
import logo from '../../Assets/logo.png';

const SIDEBAR_WIDTH = '355px';

function categoryApplicationList(cat) {
  if (!cat.application) return [];
  return String(cat.application)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function getSource(app) {
  return app?.source ?? 's';
}

function groupArticlesByCategory(articles) {
  const map = {};
  for (const article of articles) {
    const key = article.category_code || 'uncategorized';
    if (!map[key]) map[key] = [];
    map[key].push(article);
  }
  return map;
}

function articleEditorPath(article, categoryUri, source) {
  const params = new URLSearchParams();
  params.set('edit', String(article.nid));
  params.set('category', categoryUri);
  params.set('s', source);
  return `/admin/articles?${params.toString()}`;
}

function categoryArticlesPath(categoryUri, source) {
  const params = new URLSearchParams();
  params.set('category', categoryUri);
  params.set('s', source);
  return `/admin/articles?${params.toString()}`;
}

export default function AdminDocTree() {
  const [searchParams] = useSearchParams();
  const activeEditId = searchParams.get('edit');
  const [applications, setApplications] = useState([]);
  const [categories, setCategories] = useState([]);
  const [treeLoading, setTreeLoading] = useState(true);
  const [treeError, setTreeError] = useState('');
  const [expandedApps, setExpandedApps] = useState(() => new Set());
  const [expandedCategories, setExpandedCategories] = useState(() => new Set());
  const [articlesByAppId, setArticlesByAppId] = useState({});
  const [loadingAppIds, setLoadingAppIds] = useState(() => new Set());

  useEffect(() => {
    document.documentElement.style.setProperty('--sidebar-w', SIDEBAR_WIDTH);
  }, []);

  useEffect(() => {
    setTreeLoading(true);
    Promise.all([
      api.getSilent('/api/applications.php'),
      api.getSilent('/api/categories.php'),
    ])
      .then(([apps, cats]) => {
        setApplications(Array.isArray(apps) ? apps : []);
        setCategories(Array.isArray(cats) ? cats : []);
        setTreeError('');
      })
      .catch((err) => {
        setApplications([]);
        setCategories([]);
        setTreeError(err?.error || 'Failed to load documentation tree');
      })
      .finally(() => setTreeLoading(false));
  }, []);

  const categoriesByApplication = useMemo(
    () =>
      applications
        .map((app) => ({
          application: app,
          categories: categories.filter((cat) =>
            categoryApplicationList(cat).includes(app.name)
          ),
        }))
        .filter(({ categories: cats }) => cats.length > 0),
    [applications, categories]
  );

  const loadArticlesForApp = useCallback(
    async (app) => {
      if (articlesByAppId[app.id]) return articlesByAppId[app.id];
      if (loadingAppIds.has(app.id)) return null;
      setLoadingAppIds((prev) => new Set(prev).add(app.id));
      try {
        const data = await api.getSilent(
          `/api/articles.php?category=__all__&s=${encodeURIComponent(getSource(app))}`
        );
        const articles = Array.isArray(data) ? data : [];
        setArticlesByAppId((prev) => ({
          ...prev,
          [app.id]: articles,
        }));
        return articles;
      } catch {
        setArticlesByAppId((prev) => ({ ...prev, [app.id]: [] }));
        return [];
      } finally {
        setLoadingAppIds((prev) => {
          const next = new Set(prev);
          next.delete(app.id);
          return next;
        });
      }
    },
    [articlesByAppId, loadingAppIds]
  );

  useEffect(() => {
    if (!activeEditId || treeLoading || categoriesByApplication.length === 0) return;
    const nid = parseInt(activeEditId, 10);
    if (Number.isNaN(nid)) return;

    let cancelled = false;
    (async () => {
      for (const { application: app, categories: cats } of categoriesByApplication) {
        const articles = (await loadArticlesForApp(app)) || [];
        const match = articles.find((article) => Number(article.nid) === nid);
        if (!match || cancelled) continue;

        setExpandedApps((prev) => new Set(prev).add(app.id));
        const categoryUri = match.category_code || cats[0]?.uri;
        if (categoryUri) {
          setExpandedCategories((prev) => new Set(prev).add(`${app.id}:${categoryUri}`));
        }
        break;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeEditId, treeLoading, categoriesByApplication, loadArticlesForApp]);

  const toggleApp = (app) => {
    setExpandedApps((prev) => {
      const next = new Set(prev);
      if (next.has(app.id)) {
        next.delete(app.id);
      } else {
        next.add(app.id);
        loadArticlesForApp(app);
      }
      return next;
    });
  };

  const toggleCategory = (appId, categoryUri) => {
    const key = `${appId}:${categoryUri}`;
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <aside className="admin-doc-tree" aria-label="Documentation navigation">
      <div className="admin-doc-tree-header">
        <img src={logo} alt="" className="admin-doc-tree-logo" />
        <div>
          <p className="admin-doc-tree-brand">Table Mate</p>
          <p className="admin-doc-tree-title">Documentation</p>
        </div>
      </div>

      <div className="admin-doc-tree-body">
        <h3 className="admin-doc-tree-heading">Browse articles</h3>
        {treeLoading && <p className="admin-doc-tree-placeholder">Loading…</p>}
        {!treeLoading && treeError && (
          <p className="admin-doc-tree-error">{treeError}</p>
        )}
        {!treeLoading && !treeError && categoriesByApplication.length === 0 && (
          <p className="admin-doc-tree-placeholder">No documentation yet.</p>
        )}
        {!treeLoading && !treeError && (
          <ul className="admin-doc-tree-list admin-doc-tree-apps">
            {categoriesByApplication.map(({ application: app, categories: cats }) => {
              const appExpanded = expandedApps.has(app.id);
              const articlesByCategory = groupArticlesByCategory(articlesByAppId[app.id] || []);
              const appLoading = loadingAppIds.has(app.id);

              return (
                <li key={app.id} className="admin-doc-tree-app">
                  <button
                    type="button"
                    className="admin-doc-tree-branch"
                    onClick={() => toggleApp(app)}
                    aria-expanded={appExpanded}
                  >
                    <span className="admin-doc-tree-chevron" aria-hidden="true">
                      {appExpanded ? '▼' : '▶'}
                    </span>
                    <span className="admin-doc-tree-label">{app.name}</span>
                  </button>
                  {appExpanded && (
                    <ul className="admin-doc-tree-list admin-doc-tree-categories">
                      {appLoading && (
                        <li className="admin-doc-tree-placeholder-item">Loading articles…</li>
                      )}
                      {!appLoading &&
                        cats.map((cat) => {
                          const catKey = `${app.id}:${cat.uri}`;
                          const catExpanded = expandedCategories.has(catKey);
                          const articles = articlesByCategory[cat.uri] || [];

                          return (
                            <li key={cat.uri} className="admin-doc-tree-category">
                              <button
                                type="button"
                                className="admin-doc-tree-branch admin-doc-tree-branch-nested"
                                onClick={() => toggleCategory(app.id, cat.uri)}
                                aria-expanded={catExpanded}
                              >
                                <span className="admin-doc-tree-chevron" aria-hidden="true">
                                  {catExpanded ? '▼' : '▶'}
                                </span>
                                <span className="admin-doc-tree-label">
                                  {cat.labelText || cat.uri}
                                </span>
                                {articles.length > 0 && (
                                  <span className="admin-doc-tree-count">{articles.length}</span>
                                )}
                              </button>
                              {catExpanded && (
                                <ul className="admin-doc-tree-list admin-doc-tree-articles">
                                  {articles.length === 0 ? (
                                    <li className="admin-doc-tree-placeholder-item">
                                      <Link
                                        to={categoryArticlesPath(cat.uri, getSource(app))}
                                        className="admin-doc-tree-link admin-doc-tree-link-muted"
                                      >
                                        Manage articles
                                      </Link>
                                    </li>
                                  ) : (
                                    articles.map((article) => (
                                      <li key={article.nid}>
                                        <Link
                                          to={articleEditorPath(article, cat.uri, getSource(app))}
                                          className={`admin-doc-tree-link${
                                            activeEditId === String(article.nid)
                                              ? ' admin-doc-tree-link-active'
                                              : ''
                                          }`}
                                          title={article.title}
                                        >
                                          {article.title || `Article #${article.nid}`}
                                          {article.status === 0 && (
                                            <span className="admin-doc-tree-draft">Draft</span>
                                          )}
                                        </Link>
                                      </li>
                                    ))
                                  )}
                                </ul>
                              )}
                            </li>
                          );
                        })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </aside>
  );
}
